import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Send, X } from '../../icons';
import { composeMail } from '../../api/mail';
import { listMailAccounts, type MailAccount } from '../../api/external-mail';
import { Select } from '../../components/shared/Select';
// 이탈 경고 — 등록·수정이 있는 화면의 공용 약속(2026-09-08).
import { UnsavedGuard } from '../../components/shared/UnsavedGuard';
import { RecipientPicker, type PickedUser } from './RecipientPicker';
import { useAttachmentUpload } from '../../components/Attachment';
import { MailAttachDrop } from './MailAttachDrop';
import { toMessageAttachment } from '../../lib/attachments';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { tMail } from '../../lib/mail-i18n';
import { loadMailPrefs } from './mailPrefs';
// 발송 전 확인 (2026-09-05) — 목록·상세가 쓰던 그 창을 그대로 쓴다.
import { MailConfirmDialog } from './MailConfirmDialog';
// AxiEditor 로 쓰기 (2026-10-14) — 「환경설정 > 편집기 선택」이 HTML 일 때.
import { LazyAxiBoardEditor } from '../../components/editor/axi/LazyAxiBoardEditor';
import { serializeAxiForSave } from '../../components/editor/axi/axi-html';
import { initialComposeHtml } from '../../components/editor/axi/mail-html';
import { IMAGE_UPLOADING, saveBlockedByFailedImagesMessage } from '../../components/editor/editor-messages';

export interface ComposePrefill {
  /**
   * 어느 계정에서 보낼 것인가 (2026-09-07) — 답장·전달이 **받은 그 계정**을 넘긴다.
   * 안 넘기면 기본 발신 계정에서 나가서, 받는 쪽은 자기가 쓴 적 없는 주소로 답을 받는다.
   */
  sendingAccountId?: string | null;
  to?: PickedUser[];
  cc?: PickedUser[];
  bcc?: PickedUser[];
  subject?: string;
  body?: string;
  /**
   * HTML 로 쓸 때(AxiEditor) 처음 넣을 본문 (2026-10-14). 답장·전달은 원문을 HTML 그대로 인용한 것을,
   * 게시판 글 보내기는 HTML 글 본문을 넘긴다. 없으면 `body`(평문)를 HTML 로 바꿔 넣는다.
   * ★ 평문으로 쓸 때(TEXT)는 쓰지 않는다 — 그때는 지금까지처럼 `body` 다.
   */
  bodyHtml?: string;
}

interface ComposeModalProps {
  open: boolean;
  /**
   * 어떻게 서 있나 (2026-08-22).
   *
   *   modal(기본) — 지금까지 그대로. 배경을 잠그고 가운데 뜨는 창.
   *   page        — /mail/compose 한 화면. 배경 잠금·초점 가둠·배경 클릭 닫기를 하지 않고,
   *                 보내기·임시 저장을 **머리줄**로 올린다(아래 발은 안 그린다).
   *
   * ★ 값을 안 주면 한 픽셀도 달라지지 않는다 — 되돌리려면 이 속성만 빼면 된다.
   */
  variant?: 'modal' | 'page';
  /** page 일 때 머리줄에 붙는 표식 — '내게 쓰기' 처럼. */
  pageBadge?: string | null;
  /** 답장/전체답장/전달 시 prefill (open 가 true 가 되는 시점에 한 번 적용). */
  prefill?: ComposePrefill | null;
  onClose: () => void;
  /** 전송 또는 draft 저장 성공 시 호출. parent 가 list refetch 또는 toast. */
  onSent?: (result: { id: string; isDraft: boolean }) => void;
}

/**
 * MailComposeModal (P3-MAIL-03 + P3-MAIL-04 picker 결합) — 메일 작성 modal.
 *
 * 변경 (P3-MAIL-04):
 *   - 수신자: PickedUser[] (to/cc/bcc) — RecipientPicker 의 chip + 자동완성.
 *   - prefill prop — 답장/전달 시 to/subject/body 사전 주입.
 *
 * a11y: dialog role + aria-modal + ESC 닫기 (parent onClose).
 *
 * unmount transition (adr-20260506-modal-unmount-transition-css-only-pattern,
 * cycle 4): 본 modal 은 이미 `open` prop 기반 self-gating 구조이므로 hook-in-modal
 * 변형 적용. cycle 1-3 의 hook-in-parent 과 다른 valid 변형 — parent API (open prop)
 * 보존 + test 호환.
 */
/** 사내 사용자만 — 밖의 주소는 뺀다. */
function internalIds(list: ReadonlyArray<PickedUser>): string[] {
  return list.filter((u) => !u.external).map((u) => u.id);
}

/** 밖의 주소만 — 서버에는 `ext:` 접두가 붙은 id 가 아니라 **주소 그대로** 보낸다. */
function externalAddresses(list: ReadonlyArray<PickedUser>): string[] {
  return list.filter((u) => u.external).map((u) => u.email);
}

export function ComposeModal({ open, variant = 'modal', pageBadge, prefill, onClose, onSent }: ComposeModalProps) {
  const asPage = variant === 'page';
  const { shouldRender, isLeaving } = useModalLeave(open || null, 200);
  // 화면일 때는 가두지도 잠그지도 않는다 — 뒤에 잠글 화면이 없다.
  const trapRef = useFocusTrap<HTMLDivElement>(shouldRender && !asPage);
  useBodyScrollLock(shouldRender && !asPage);
  const [to, setTo] = useState<PickedUser[]>([]);
  const [cc, setCc] = useState<PickedUser[]>([]);
  const [bcc, setBcc] = useState<PickedUser[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  /**
   * HTML 로 쓰는가 (2026-10-14 · AxiEditor). 「환경설정 > 기본환경 > 편집기 선택」(composeMode)을 따른다.
   * ★ 읽기 전(null)과 TEXT 는 지금까지의 textarea 다 — 설정을 못 읽어도 쓰기가 막히지 않는다.
   */
  const [htmlMode, setHtmlMode] = useState<boolean | null>(null);
  /** HTML 로 열 때 넣은 본문 — «사람이 고쳤나»(이탈 경고)를 이것과 견준다. */
  const [htmlBaseline, setHtmlBaseline] = useState('');
  /**
   * 편집기는 제어되지 않으므로, 다시 열 때마다 새로 만든다.
   * ★ 0 이면 «첫 본문이 아직 안 정해졌다» — 그동안은 편집기를 그리지 않는다(빈 본문으로 한 번
   *   만들었다가 곧바로 다시 만드는 일을 막는다).
   */
  const [editorKey, setEditorKey] = useState(0);
  const [busy, setBusy] = useState<'send' | 'draft' | null>(null);
  /**
   * 보내는 계정 (2026-09-07).
   *
   * ★ 계정 목록은 **열 때 한 번** 읽는다. 실패해도 조용히 빈 목록 — 계정 목록을 못 읽었다고
   *   작성 자체가 막히면 안 된다(그때는 지금까지처럼 서버가 기본 계정을 고른다).
   */
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [sendingAccountId, setSendingAccountId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * 아직 수신자로 확정되지 않은 입력 (2026-08-20).
   *
   * RecipientPicker 는 **목록에서 골라야** 칩이 된다. 그 사실이 화면에 없어서, 주소를 다 적고
   * 보낸 메일이 받는사람 0명으로 나간 적이 있다(mail_messages id=29 — TO 0 · CC 1 · BCC 1).
   * 참조에 한 명만 있으면 아래 no_recipients 검사를 통과해 버리는 것이 그 경로였다.
   */
  const [pending, setPending] = useState<{ to: string; cc: string; bcc: string }>({ to: '', cc: '', bcc: '' });

  // attachment upload — 직전 사이클 신규 학습 (pattern-shared-component-extraction-cross-domain
  // -7-atomic) 의 *2번째 정착 사례*. chat MessageInput 의 logic 과 동일 — hook + component 재사용.
  const uploadState = useAttachmentUpload();
  const { attachments, uploadingFile, clearAttachments } = uploadState;

  // F2 (review-findings Loop1) — RecipientPicker 의 useEffect deps churn 회피.
  // useMemo 없으면 매 렌더 새 array ref 가 RecipientPicker 의 [query, value, excludeIds] deps 를
  // churn 시켜 setTimeout cancel/reschedule cycle burst + setLoading/setResults 비용 누적.
  const excludeForTo = useMemo(
    () => Array.from(new Set([...cc, ...bcc].map((u) => u.id))),
    [cc, bcc],
  );
  const excludeForCc = useMemo(
    () => Array.from(new Set([...to, ...bcc].map((u) => u.id))),
    [to, bcc],
  );
  const excludeForBcc = useMemo(
    () => Array.from(new Set([...to, ...cc].map((u) => u.id))),
    [to, cc],
  );

  const setPendingTo = useCallback((v: string) => setPending((p) => (p.to === v ? p : { ...p, to: v })), []);
  const setPendingCc = useCallback((v: string) => setPending((p) => (p.cc === v ? p : { ...p, cc: v })), []);
  const setPendingBcc = useCallback((v: string) => setPending((p) => (p.bcc === v ? p : { ...p, bcc: v })), []);
  const hasPending = pending.to !== '' || pending.cc !== '' || pending.bcc !== '';

  /**
   * 잃을 것이 있는가 — 이탈 경고의 재료 (2026-09-08 사용자 지시).
   *
   * ★★★ **«비어 있지 않다» 가 아니라 «사람이 고쳤다» 로 잰다.** 답장·전달은 열자마자
   *    받는사람 · 제목 · 인용 본문이 차 있다 — 그것을 «쓰던 것» 으로 세면 **아무것도 안 하고
   *    나가는 사람에게까지 창이 뜬다.** 그래서 prefill 이 넣어 둔 값과 **견준다.**
   * ★ 첨부는 올린 순간 잃을 것이 된다(되돌리려면 다시 올려야 한다).
   * ★ 아직 칩이 안 된 입력(`pending`)도 잃을 것이다 — 화면에는 글자가 보이기 때문이다.
   */
  const idsOf = (xs: readonly PickedUser[]) => xs.map((u) => u.id).join(',');
  const dirty =
    idsOf(to) !== idsOf(prefill?.to ?? [])
    || idsOf(cc) !== idsOf(prefill?.cc ?? [])
    || idsOf(bcc) !== idsOf(prefill?.bcc ?? [])
    || subject !== (prefill?.subject ?? '')
    || body !== (htmlMode ? htmlBaseline : (prefill?.body ?? ''))
    || attachments.length > 0
    || hasPending;

  // open 가 true 로 전환될 때 prefill 적용. open 이 false 일 때는 reset 안 함 (close 시점에 reset 처리).
  useEffect(() => {
    if (!open) return;
    setTo(prefill?.to ?? []);
    setCc(prefill?.cc ?? []);
    setBcc(prefill?.bcc ?? []);
    setSubject(prefill?.subject ?? '');
    setBody(prefill?.body ?? '');
    setSendingAccountId(prefill?.sendingAccountId ?? null);
    setError(null);
    setPending({ to: '', cc: '', bcc: '' });
  }, [open, prefill]);

  // 쓰기 방식 — 열 때 읽는다(loadMailPrefs 는 한 번 읽은 것을 나눠 쓴다).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    loadMailPrefs()
      .then((p) => { if (alive) setHtmlMode(p.composeMode === 'HTML'); })
      .catch(() => { if (alive) setHtmlMode(false); });
    return () => { alive = false; };
  }, [open]);

  // HTML 로 쓸 때 처음 본문 — prefill 의 HTML(없으면 평문을 HTML 로)을 편집기에 넣는다.
  useEffect(() => {
    if (!open || htmlMode !== true) return;
    const html = initialComposeHtml(prefill);
    setBody(html);
    setHtmlBaseline(html);
    setEditorKey((k) => k + 1);
  }, [open, prefill, htmlMode]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    listMailAccounts()
      .then((rows) => { if (alive) setAccounts(rows); })
      .catch(() => { if (alive) setAccounts([]); });
    return () => { alive = false; };
  }, [open]);

  /** 실제로 보낼 수 있는 계정 — SMTP 칸이 다 찬 것만. 서버의 판정과 같은 조건이다. */
  const sendable = useMemo(
    () => accounts.filter((a) => a.smtpHost && a.smtpPort && a.smtpUser && a.hasSmtpPass),
    [accounts],
  );
  /** 고른 것이 보낼 수 있는 계정이면 그것, 아니면 기본 계정(없으면 첫 번째). */
  const chosen = useMemo(() => (
    sendable.find((a) => a.id === sendingAccountId)
      ?? sendable.find((a) => a.isDefaultSender)
      ?? sendable[0]
      ?? null
  ), [sendable, sendingAccountId]);
  /**
   * ★★ **받은 계정으로는 못 보내는 경우를 말한다.** 네이버로 받았는데 그 계정에 SMTP 가 없으면
   *   기본 계정에서 나간다 — 조용히 그러면 받는 쪽만 이상해진다. 화면이 미리 알린다.
   */
  const unusablePrefill = useMemo(() => {
    const want = prefill?.sendingAccountId;
    if (!want || sendable.some((a) => a.id === want)) return null;
    return accounts.find((a) => a.id === want) ?? null;
  }, [accounts, prefill?.sendingAccountId, sendable]);

  const reset = useCallback(() => {
    setTo([]); setCc([]); setBcc([]);
    setSubject(''); setBody('');
    setEditorKey(0);
    setError(null);
    setPending({ to: '', cc: '', bcc: '' });
    clearAttachments();
  }, [clearAttachments]);

  // 닫기 — 업로드 중이면 inflight abort + state 정리 (pattern-react-sse-hook-with-cancelled-flag).
  const handleClose = useCallback(() => {
    // ★ 화면(page)일 때는 비우지 않고 나간다 (2026-10-18). 먼저 비우면 이탈 경고가 «잃을 것 없음»으로 읽어
    //   쓰던 것을 묻지 않고 버린다(그리고 그 이동이 막혔다 풀리며 취소돼 「목록」이 먹지 않았다).
    //   화면은 나가면 통째로 내려가니 비울 것도 없다. 창(modal)은 다시 열리므로 종전대로 비운다.
    if (!asPage) reset();
    onClose();
  }, [asPage, onClose, reset]);

  useEscapeClose(handleClose, busy !== null);

  /**
   * 발송 전 확인 팝업 (2026-09-05) — 「환경설정 > 기본환경 > 발송 전 확인 팝업」.
   *
   * ★ 확인은 **보내기에만** 붙는다. 임시 저장은 되돌릴 수 있는 일이라 묻지 않는다.
   * ★ 물어본 뒤 다시 부를 때는 `confirmed` 로 들어와 같은 길을 그대로 탄다 —
   *   보내는 코드를 두 벌로 만들면 한쪽만 고쳐지는 날이 온다.
   */
  const [askSend, setAskSend] = useState(false);

  const handleSubmit = useCallback(async (isDraft: boolean, confirmed = false) => {
    if (busy || uploadingFile) return;  // 업로드 중에는 send 불가 (chat MessageInput canSend 와 동일)
    setBusy(isDraft ? 'draft' : 'send');
    setError(null);
    try {
      if (!isDraft && to.length === 0 && cc.length === 0 && bcc.length === 0) {
        setError(tMail('mail.compose.err.no_recipients'));
        return;
      }
      if (!subject.trim()) {
        setError(tMail('mail.compose.err.no_subject'));
        return;
      }

      // 받는사람·제목이 갖춰진 뒤에 묻는다 — 빈 폼을 두고 「이대로 보낼까요」는 말이 안 된다.
      if (!isDraft && !confirmed && (await loadMailPrefs()).sendConfirm) {
        setAskSend(true);
        return;   // finally 가 busy 를 되돌린다
      }

      /*
       * HTML(AxiEditor) — 저장할 꼴로 바꾸고, 덜 올라간 그림이 있으면 막는다(게시판 글쓰기와 같은 규칙).
       * 본문 그림은 앱 주소로 저장되고, 밖으로 나갈 때 서버가 cid 인라인 첨부로 싣는다.
       */
      let bodyToSend = body;
      if (htmlMode) {
        const saved = serializeAxiForSave(body);
        if (saved.uploadingCount > 0) { setError(IMAGE_UPLOADING); return; }
        if (saved.failedCount > 0) { setError(saveBlockedByFailedImagesMessage(saved.failedCount)); return; }
        bodyToSend = saved.html;
      }

      const res = await composeMail({
        subject: subject.trim(),
        body: bodyToSend,
        // 쓰기 방식(migration 110) — **보낼 때** 읽는다. 이 모달은 게시판 화면도 닫힌 채로 이고
        // 있어서, 마운트 때 읽으면 게시판을 열 때마다 설정 요청이 한 번씩 나간다.
        // loadMailPrefs 는 한 번 부른 것을 나눠 쓰고, 실패하면 기본값('TEXT')으로 떨어진다.
        bodyType: (await loadMailPrefs()).composeMode,
        isDraft,
        // 사내 사용자와 밖의 주소를 갈라 보낸다 (migration 113).
        // ★ 밖의 주소는 id 가 `ext:` 로 시작한다 — 사용자 id 칸에 섞여 들어가면 서버가
        //   "그런 수신자 없음" 으로 통째로 거절한다.
        recipientUserIds: {
          to: internalIds(to),
          cc: internalIds(cc),
          bcc: internalIds(bcc),
        },
        externalRecipients: {
          to: externalAddresses(to),
          cc: externalAddresses(cc),
          bcc: externalAddresses(bcc),
        },
        // 고를 것이 둘 이상일 때만 담는다 — 하나뿐이면 고를 것이 없고, 이 칸을 모르는 옛 서버에
        // 400 을 맞을 이유도 없다.
        sendingAccountId: sendable.length >= 2 && chosen ? chosen.id : undefined,
        attachments: attachments.length > 0 ? attachments.map(toMessageAttachment) : undefined,
      });
      reset();
      onSent?.({ id: res.id, isDraft: res.isDraft });
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { code?: string; message?: string } } };
      setError(e.response?.data?.message ?? (err instanceof Error ? err.message : 'send_failed'));
    } finally {
      setBusy(null);
    }
  }, [attachments, body, busy, cc, bcc, htmlMode, onClose, onSent, reset, subject, to, uploadingFile]);

  /** 확인 창이 쓰는 문구 — 무엇이 어디로 가는지 적는다. */
  const confirmDesc = tMail('mail.send.confirm_desc')
    .replace('{s}', subject.trim() || tMail('mail.item.subject_empty'))
    .replace('{n}', String(to.length + cc.length + bcc.length));

  if (!shouldRender) return null;

  /* 보내기·임시 저장은 한 벌만 만든다 — 창일 때는 발에, 화면일 때는 머리줄에 놓는다.
     두 벌로 두면 한쪽만 고쳐지는 날이 온다. */
  const draftButton = (
    <button
      type="button"
      className="mail-compose-button mail-compose-button--secondary"
      onClick={() => void handleSubmit(true)}
      disabled={busy !== null}
      aria-busy={busy === 'draft'}
    >
      {busy === 'draft' ? tMail('mail.compose.btn.saving') : tMail('mail.compose.btn.save_draft')}
    </button>
  );
  /* 받는사람이 비면 아예 못 누르게 한다. 참조만 있어도 열려 있던 것이 id=29 의 경로였다.
     누를 수 있을 때는 **몇 명에게 가는지**를 버튼에 적는다 — 눌러 보기 전에 알아야 한다. */
  const sendButton = (
    <button
      type="button"
      className="mail-compose-button mail-compose-button--primary"
      onClick={() => void handleSubmit(false)}
      disabled={busy !== null || to.length === 0}
      aria-busy={busy === 'send'}
    >
      <Send size={14} aria-hidden />{' '}
      {busy === 'send'
        ? tMail('mail.compose.btn.sending')
        : to.length === 0
          ? tMail('mail.compose.btn.send_need_to')
          : tMail('mail.compose.btn.send_count').replace('{n}', String(to.length + cc.length + bcc.length))}
    </button>
  );

  const fields = (
    <div className="mail-compose-body">
      {/*
        ★ 2026-08-23 — 여태 'To' · 'CC' · 'BCC' 가 코드에 박혀 있어 **어느 언어로 봐도 영어**였다.
          상세가 쓰던 낱말(받는사람·참조·숨은참조)을 그대로 함께 쓴다.
        ★ `fieldKey` 를 따로 준다 — 목록 id 를 라벨에서 뽑고 있어서, 라벨이 번역되면 id 까지
          언어마다 달라진다(`recipient-list-받는사람`). id 는 언어와 무관해야 한다.
      */}
      {sendable.length > 0 && (
        <div className="mail-compose-field">
          <span className="mail-compose-field-label">{tMail('mail.compose.label_from')}</span>
          {sendable.length === 1 ? (
            // 고를 것이 없으면 고르게 하지 않는다 — 어디서 나가는지만 적는다.
            <span className="mail-compose-from-fixed">{sendable[0]!.email}</span>
          ) : (
            <Select
              value={chosen?.id ?? null}
              onChange={setSendingAccountId}
              options={sendable.map((a) => ({ value: a.id, label: a.email }))}
              ariaLabel={tMail('mail.compose.label_from')}
              /* ★ 모달·표 안의 팝오버는 overflow 상자에 잘린다 — portal 로 띄운다. */
              portal
            />
          )}
        </div>
      )}
      {unusablePrefill && (
        <p className="mail-compose-from-warn">
          {tMail('mail.compose.from_unusable')
            .replace('{from}', unusablePrefill.email)
            .replace('{fallback}', chosen?.email ?? '')}
        </p>
      )}
      <RecipientPicker fieldKey="to" label={tMail('mail.field.to')} value={to} onChange={setTo} excludeIds={excludeForTo} onPendingChange={setPendingTo} autoFocus={!asPage} />
      <RecipientPicker fieldKey="cc" label={tMail('mail.field.cc')} value={cc} onChange={setCc} excludeIds={excludeForCc} onPendingChange={setPendingCc} />
      <RecipientPicker fieldKey="bcc" label={tMail('mail.field.bcc')} value={bcc} onChange={setBcc} excludeIds={excludeForBcc} onPendingChange={setPendingBcc} />
      <div className="mail-compose-field">
        <span className="mail-compose-field-label">{tMail('mail.compose.label_subject')}</span>
        <input
          className="mail-compose-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={tMail('mail.compose.placeholder_subject')}
          aria-label={tMail('mail.compose.aria_subject')}
          required
        />
      </div>
      {htmlMode ? (
        <div className="mail-compose-editor">
          {editorKey > 0 && (
          <LazyAxiBoardEditor
            key={editorKey}
            initialHtml={htmlBaseline}
            onChangeHtml={setBody}
            contentMaxLength={null}
            ariaLabel={tMail('mail.compose.aria_body')}
          />
          )}
        </div>
      ) : (
      <textarea
        className="mail-compose-textarea"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={tMail('mail.compose.placeholder_body')}
        rows={10}
        aria-label={tMail('mail.compose.aria_body')}
      />
      )}
      {/* 2026-08-22 — 메일은 여러 개·끌어놓기·워크스페이스가 필요해 전용 자리를 쓴다.
          채팅·게시판의 AttachmentUploader 는 그대로 두고, 알맹이(hook)만 나눠 쓴다. */}
      <MailAttachDrop state={uploadState} disabled={busy !== null} />
      {hasPending && (
        <div className="mail-compose-warn" role="status">{tMail('mail.compose.warn.pending')}</div>
      )}
      {error && <div className="mail-compose-error" role="status">{error}</div>}
    </div>
  );

  /* ── 화면으로 서 있을 때 (/mail/compose) ────────────────────────────────
   * 배경도 잠금도 없다. 도구는 머리줄 한 줄에 모으고, 아래 발은 그리지 않는다.
   */
  /* 한 벌만 만든다 — 화면판·창판이 나눠 쓴다. 두 벌이면 한쪽만 고쳐지는 날이 온다. */
  const sendConfirmDialog = (
    <MailConfirmDialog
      open={askSend}
      title={tMail('mail.send.confirm_title')}
      description={confirmDesc}
      confirmLabel={tMail('mail.send.confirm_ok')}
      busy={busy === 'send'}
      onConfirm={() => { setAskSend(false); void handleSubmit(false, true); }}
      onCancel={() => setAskSend(false)}
    />
  );

  if (asPage) {
    return (
      <section className="mail-compose-page" aria-label={tMail('mail.compose.heading')}>
        {/*
          ★★★ 쓰던 것을 두고 나가려 하면 묻는다 (2026-09-08 사용자 지시).
             작성이 «셸 안의 오른쪽 칸» 이 되면서(#1048) 좌측 메일함이 늘 보이게 됐고,
             그만큼 **초안을 버리고 나가는 길이 늘었다.** 머리줄 「목록」 · 좌측 메일함 ·
             뒤로가기 · 새로고침이 전부 이 하나로 막힌다.
        */}
        <UnsavedGuard when={dirty} description={tMail('mail.compose.unsaved_desc')} />
        <header className="mail-compose-pagebar">
          <button type="button" className="mail-compose-back" onClick={handleClose}>
            <ArrowLeft size={15} aria-hidden />
            {tMail('mail.compose.back_to_list')}
          </button>
          <h1 className="mail-compose-pagetitle">
            {tMail('mail.compose.heading')}
            {pageBadge && <span className="mail-compose-pagebadge">{pageBadge}</span>}
          </h1>
          <span className="mail-compose-pagegap" aria-hidden />
          {draftButton}
          {sendButton}
        </header>
        {fields}
        {sendConfirmDialog}
      </section>
    );
  }

  return (
    <div
      className={`mail-compose-backdrop${isLeaving ? ' mail-compose-backdrop--leaving' : ''}`}
      onClick={isLeaving ? undefined : handleClose}
      role="presentation"
      aria-hidden={isLeaving || undefined}
    >
      <div
        ref={trapRef}
        className={`mail-compose-modal${isLeaving ? ' mail-compose-modal--leaving' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={tMail('mail.compose.heading')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mail-compose-header">
          <h2 className="mail-compose-title">{tMail('mail.compose.heading')}</h2>
          <button type="button" className="mail-compose-close" onClick={handleClose} aria-label={tMail('mail.compose.close_aria')}>
            <X size={18} />
          </button>
        </header>

        {fields}

        <footer className="mail-compose-footer">
          {draftButton}
          {sendButton}
        </footer>
      </div>
      {sendConfirmDialog}
    </div>
  );
}

